import { useCallback, useEffect, useRef, useState } from "react";
import { getInitialView, SUB_VIEWS } from "./view";
import { scrollPageToTop } from "./scrollTop";

const HISTORY_KEY = "officialWebsiteView";

function readLocation() {
  const params = new URLSearchParams(window.location.search);
  const view = getInitialView();
  const saved = window.history.state?.[HISTORY_KEY];
  const newsId = view === "news" ? (params.get("newsId") || "").trim() : "";
  // URL 保存当前文章，完整刷新或直接打开链接时不依赖 history.state。
  if (newsId) {
    return {
      view,
      newsDetailId: newsId,
      newsDetailTitle: saved?.view === "news" && saved.newsDetailId === newsId
        ? saved.newsDetailTitle || ""
        : "",
    };
  }
  if (
    saved && (saved.view === "landing" || SUB_VIEWS.includes(saved.view)) &&
    (!params.has("view") || saved.view === view)
  ) {
    return saved;
  }
  return { view, newsDetailId: null, newsDetailTitle: "" };
}

function getPageUrl({ view, newsDetailId }) {
  const url = new URL(window.location.href);
  if (view === "landing") url.searchParams.delete("view");
  else url.searchParams.set("view", view);
  if (view === "news" && newsDetailId) url.searchParams.set("newsId", newsDetailId);
  else url.searchParams.delete("newsId");
  return url;
}

/** PC / Pad / Mobile 共用视图历史；预览在 iframe 内切换，不写入浏览器历史。 */
export default function useWebsiteNavigation({
  previewMode = false,
  previewNewsId = null,
} = {}) {
  const [page, setPage] = useState(() =>
    previewMode
      ? { view: "news", newsDetailId: previewNewsId, newsDetailTitle: "" }
      : readLocation()
  );
  const pageRef = useRef(page);

  useEffect(() => {
    if (previewMode) return undefined;
    // 浏览器在 popstate 之后恢复旧滚动位置，会覆盖 App 的 useLayoutEffect 置顶。
    const previousScrollRestoration = window.history.scrollRestoration;
    window.history.scrollRestoration = "manual";
    let scrollFrame = null;
    // 给首次访问的列表 / 首页补上状态，后退时才能恢复它，而不是离开官网。
    window.history.replaceState(
      { ...window.history.state, [HISTORY_KEY]: pageRef.current },
      "",
      getPageUrl(pageRef.current)
    );
    const onPopState = () => {
      if (scrollFrame !== null) {
        window.cancelAnimationFrame(scrollFrame);
        scrollFrame = null;
      }
      const restored = readLocation();
      pageRef.current = restored;
      setPage(restored);
      // 即使两个历史条目的视图 / 资讯 id 相同，也需要在历史切换结束后置顶。
      if (restored.view === "news") {
        scrollFrame = window.requestAnimationFrame(() => {
          scrollFrame = null;
          if (pageRef.current === restored) scrollPageToTop();
        });
      }
    };
    window.addEventListener("popstate", onPopState);
    return () => {
      window.removeEventListener("popstate", onPopState);
      if (scrollFrame !== null) window.cancelAnimationFrame(scrollFrame);
      window.history.scrollRestoration = previousScrollRestoration;
    };
  }, [previewMode]);

  const navigate = useCallback((view, newsDetailId = null, newsDetailTitle = "") => {
    const next = { view, newsDetailId, newsDetailTitle };
    const current = pageRef.current;
    if (
      current.view === view &&
      current.newsDetailId === newsDetailId &&
      current.newsDetailTitle === newsDetailTitle
    ) return;

    if (!previewMode) {
      window.history.pushState(
        { ...window.history.state, [HISTORY_KEY]: next },
        "",
        getPageUrl(next)
      );
    }
    pageRef.current = next;
    setPage(next);
  }, [previewMode]);

  return { ...page, navigate };
}
